import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  FileText, 
  Shield, 
  Eye, 
  Clock,
  User,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AccessLog() {
  const { token } = useAuth();
  const [accessSummary, setAccessSummary] = useState(null);
  const [accessLogs, setAccessLogs] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [supportSettings, setSupportSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [orgId, setOrgId] = useState(null);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // First get user's org
      const orgsRes = await fetch(`${API_URL}/api/org/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const orgs = await orgsRes.json();
      
      if (orgs.length > 0) {
        const firstOrgId = orgs[0].id;
        setOrgId(firstOrgId);
        
        // Fetch support settings
        const settingsRes = await fetch(`${API_URL}/api/support-access/${firstOrgId}/settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (settingsRes.ok) {
          setSupportSettings(await settingsRes.json());
        }
        
        // Fetch access summary
        const summaryRes = await fetch(`${API_URL}/api/support-access/${firstOrgId}/summary`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (summaryRes.ok) {
          setAccessSummary(await summaryRes.json());
        }
        
        // Fetch active sessions
        const sessionsRes = await fetch(`${API_URL}/api/support-access/${firstOrgId}/sessions`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (sessionsRes.ok) {
          setSessions(await sessionsRes.json());
        }
        
        // Fetch access logs
        const logsRes = await fetch(`${API_URL}/api/support-access/${firstOrgId}/logs?limit=30`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (logsRes.ok) {
          setAccessLogs(await logsRes.json());
        }
      }
    } catch (err) {
      setError('Unable to load access logs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleSupportAccess = async () => {
    if (!orgId || updating) return;
    
    try {
      setUpdating(true);
      const newValue = !supportSettings?.allow_support_access;
      
      const res = await fetch(`${API_URL}/api/support-access/${orgId}/settings`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ allow_support_access: newValue })
      });
      
      if (res.ok) {
        setSupportSettings(await res.json());
        // Refresh data to get updated session info
        fetchData();
      }
    } catch (err) {
      setError('Failed to update settings');
    } finally {
      setUpdating(false);
    }
  };

  const revokeSession = async (sessionId) => {
    if (!orgId) return;
    
    try {
      const res = await fetch(`${API_URL}/api/support-access/${orgId}/sessions/${sessionId}/revoke`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        fetchData();
      }
    } catch (err) {
      setError('Failed to revoke session');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="support-page">
        <div className="support-container">
          <div className="loading-state">
            <RefreshCw className="spin" size={24} />
            <p>Loading access logs...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="support-page" data-testid="access-log-page">
      <div className="support-container">
        <Link to="/support" className="support-back-link">
          <ArrowLeft size={16} />
          Back to Support
        </Link>

        <div className="support-header">
          <div className="support-icon">
            <FileText size={28} />
          </div>
          <h1>Audit & Access Log</h1>
          <p className="support-subtitle">
            View all TradeLife support access to your organisation
          </p>
        </div>

        {error && (
          <div className="support-error">
            <AlertTriangle size={18} />
            <span>{error}</span>
          </div>
        )}

        {/* Support Access Toggle */}
        <div className="support-card access-toggle-card" data-testid="access-toggle">
          <div className="access-toggle-header">
            <div className="toggle-info">
              <h3>Allow TradeLife Support Access</h3>
              <p>
                When enabled, TradeLife support can access technical system status to help resolve issues. 
                They cannot access your financial data, documents, or communications.
              </p>
            </div>
            <button 
              className={`toggle-btn ${supportSettings?.allow_support_access ? 'enabled' : ''}`}
              onClick={toggleSupportAccess}
              disabled={updating}
              data-testid="support-access-toggle"
            >
              {supportSettings?.allow_support_access ? (
                <ToggleRight size={32} />
              ) : (
                <ToggleLeft size={32} />
              )}
            </button>
          </div>
          <div className="access-status">
            {supportSettings?.allow_support_access ? (
              <span className="status enabled">
                <CheckCircle size={14} />
                Support access enabled
              </span>
            ) : (
              <span className="status disabled">
                <XCircle size={14} />
                Support access disabled
              </span>
            )}
          </div>
        </div>

        {/* Access Summary */}
        {accessSummary && (
          <div className="support-card" data-testid="access-summary">
            <h3 className="card-title">
              <Eye size={18} />
              Access Summary
            </h3>
            <div className="access-summary-grid">
              <div className="summary-item">
                <span className="summary-value">{accessSummary.active_sessions}</span>
                <span className="summary-label">Active Sessions</span>
              </div>
              <div className="summary-item">
                <span className="summary-value">{accessSummary.total_access_logs}</span>
                <span className="summary-label">Total Actions Logged</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Last Access</span>
                <span className="summary-value small">{formatDate(accessSummary.last_access_at)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Active Sessions */}
        {sessions.length > 0 && (
          <div className="support-card" data-testid="active-sessions">
            <h3 className="card-title">
              <User size={18} />
              Active Support Sessions
            </h3>
            <div className="sessions-list">
              {sessions.map((session) => (
                <div key={session.id} className="session-item">
                  <div className="session-info">
                    <span className="session-email">{session.support_user_email}</span>
                    <span className="session-reason">Reason: {session.reason}</span>
                    <span className="session-time">
                      <Clock size={12} />
                      Expires: {formatDate(session.expires_at)}
                    </span>
                  </div>
                  <button 
                    className="revoke-btn"
                    onClick={() => revokeSession(session.id)}
                    data-testid={`revoke-session-${session.id}`}
                  >
                    <XCircle size={14} />
                    Revoke
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Access Logs */}
        <div className="support-card" data-testid="access-logs">
          <h3 className="card-title">
            <FileText size={18} />
            Access Log History
          </h3>
          {accessLogs.length > 0 ? (
            <div className="logs-list">
              {accessLogs.map((log) => (
                <div key={log.id} className="log-item">
                  <div className="log-header">
                    <span className="log-action">{log.action.replace(/_/g, ' ')}</span>
                    <span className="log-time">{formatDate(log.timestamp)}</span>
                  </div>
                  {log.target_type && (
                    <span className="log-target">
                      Target: {log.target_type} {log.target_id ? `(${log.target_id.slice(0, 8)}...)` : ''}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-logs">
              <p>No support access logs yet.</p>
            </div>
          )}
        </div>

        {/* Trust Notice */}
        <div className="support-trust-notice" data-testid="trust-notice">
          <Shield size={18} />
          <p>
            TradeLife support can only access technical system status — never your financial data or documents. 
            Any access is logged and visible to you.
          </p>
        </div>
      </div>
    </div>
  );
}

export default AccessLog;
